import React from 'react';
import { Hero2Column } from '@/components/sections/Hero/Hero2Column';
import { Leadership } from '@/components/sections/Leadership/Leadership';
import { Investors } from '@/components/sections/Investors/Investors';
import { Careers } from '@/components/sections/Careers/Careers';
import { CTA } from '@/components/sections/CTA/CTA';
import { Recognition } from '@/components/sections/Recognition/Recognition';
import Image from '@/components/common/Image';
import { Button } from '@/components/common/Button/Button';
import PageWrapper from '@/components/layout/PageWrapper';
import { META } from '@/config/constants';
import { leadershipMembers, investors, careerImages, recognitionBadges, aboutData } from '@/config/aboutData';

const AboutPage: React.FC = () => {
  return (
    <PageWrapper
      title={META.ABOUT.TITLE}
      description={META.ABOUT.DESCRIPTION}
      keywords={["about Cobalt", "embedded iPaaS mission", "integration platform", "SaaS integration company"]}
    >
      <div className="text-center md:text-left">
        <Hero2Column
          title={aboutData.hero.title}
          subtext={aboutData.hero.subtext}
          image="/about-us/about-us-hero.png"
          tagHref="#"
        />

        <Leadership
          title={aboutData.leadership.title}
          description={aboutData.leadership.description}
          members={leadershipMembers}
        />

        <Investors
          title={aboutData.investors.title}
          description={aboutData.investors.description}
          investors={investors}
        />

        <Careers
          tag={aboutData.careers.tag}
          title={aboutData.careers.title}
          description={aboutData.careers.description}
          buttonText={aboutData.careers.buttonText}
          buttonHref={aboutData.careers.buttonHref}
          images={careerImages}
        />

        <Recognition
          tag={aboutData.recognition.tag}
          title={aboutData.recognition.title}
          description={aboutData.recognition.description}
          badges={recognitionBadges}
        />

        {/* OvationCXM Case Study */}
        <section className="py-12 md:py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              {/* Mobile view - stacked layout */}
              <div className="block md:hidden">
                <div className="bg-[#f2f6ff] rounded-xl p-8">
                  <h2 className="text-2xl font-bold text-center mb-4">{aboutData.caseStudy.title}</h2>
                  <p className="text-gray-700 text-center mb-6">
                    {aboutData.caseStudy.description}
                  </p>
                  <div className="text-center mb-6">
                    <p className="font-medium text-lg">{aboutData.caseStudy.personName}</p>
                    <p className="text-gray-500">{aboutData.caseStudy.personTitle}</p>
                  </div>
                  <div className="flex justify-center mb-8">
                    <Button
                      variant="solid"
                      color="primary"
                      onClick={() => {
                        window.open(aboutData.caseStudy.buttonUrl, '_blank');
                      }}
                    >
                      {aboutData.caseStudy.buttonText}
                    </Button>
                  </div>
                  <div className="relative">
                    <Image
                      src={aboutData.caseStudy.imageSrc}
                      alt={aboutData.caseStudy.imageAlt}
                      className="w-full rounded-lg object-contain"
                      width={500}
                      height={300}
                    />
                  </div>
                </div>
              </div>

              {/* Desktop view - side by side layout */}
              <div className="hidden md:block">
                <div className="bg-[#f2f6ff] rounded-xl p-12 flex items-center">
                  <div className="w-1/2 pr-16 flex flex-col justify-center">
                    <h2 className="text-3xl font-bold mb-6">{aboutData.caseStudy.title}</h2>
                    <p className="text-gray-700 mb-8">
                      {aboutData.caseStudy.description}
                    </p>
                    <div className="mb-8">
                      <p className="font-medium text-lg">{aboutData.caseStudy.personName}</p>
                      <p className="text-gray-500">{aboutData.caseStudy.personTitle}</p>
                    </div>
                    <div>
                      <Button
                        variant="solid"
                        color="primary"
                        onClick={() => {
                          window.open(aboutData.caseStudy.buttonUrl, '_blank');
                        }}
                      >
                        {aboutData.caseStudy.buttonText}
                      </Button>
                    </div>
                  </div>
                  <div className="w-1/2">
                    <Image
                      src={aboutData.caseStudy.imageSrc}
                      alt={aboutData.caseStudy.imageAlt}
                      className="w-full rounded-lg object-cover"
                      width={600}
                      height={400}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <CTA
          title={aboutData.cta.title}
          description={aboutData.cta.description}
          cta={aboutData.cta.cta}
        />
      </div>
    </PageWrapper>
  );
};

export default AboutPage;